<template>
  <section class="flex flex-col gap-4 rounded-lg border p-4">
    <header class="flex items-center justify-between gap-3">
      <h3 class="text-base font-semibold text-foreground">{{ title }}</h3>
      <span v-if="attachedTags.length > 0" class="text-xs text-muted-foreground">{{ attachedTags.length }} linked</span>
    </header>

    <p v-if="loadErrorMessage" class="text-sm text-destructive">
      {{ loadErrorMessage }}
    </p>

    <div v-if="attachedTags.length > 0" class="flex flex-wrap gap-2">
      <Badge v-for="tag in attachedTags" :key="tag.id" variant="outline" :style="resolveTagStyle(tag)" class="gap-1.5">
        <span>{{ tag.name }}</span>
        <button v-if="!readonly" type="button" class="rounded-sm text-muted-foreground hover:text-foreground disabled:cursor-wait disabled:opacity-50" :disabled="tagsStore.isDetachingTag(tag.id)" :aria-label="`Remove ${tag.name}`" @click="detachTag(tag)">
          <X class="h-3 w-3" />
        </button>
      </Badge>
    </div>
    <p v-else-if="!tagsStore.isLoading" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">No tags are linked to this record yet.</p>

    <div v-if="!readonly" class="flex flex-col gap-3">
      <div class="flex flex-col gap-2 sm:flex-row">
        <Input v-model="tagQuery" size="sm" placeholder="Search or create tag" @keydown.enter.prevent="addTypedTag" />
        <Button type="button" size="sm" :disabled="!canAddTypedTag || isAddingTypedTag" :loading="isAddingTypedTag" @click="addTypedTag">
          Add Tag
        </Button>
      </div>

      <div v-if="matchingAvailableTags.length > 0" class="flex flex-wrap gap-2">
        <Button v-for="tag in matchingAvailableTags" :key="tag.id" type="button" size="xs" variant="outline" :disabled="tagsStore.isAttachingTag(tag.id)" :loading="tagsStore.isAttachingTag(tag.id)" @click="attachTag(tag)">
          {{ tag.name }}
        </Button>
      </div>
    </div>
  </section>
</template>

<script setup>
import axios from 'axios'
import { X } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { Badge } from '@/components/ui/badge/index.js'
import { Button } from '@/components/ui/button/index.js'
import { Input } from '@/components/ui/input/index.js'
import { useTagsStore } from '../../stores/tagsStore.js'

const emit = defineEmits(['changed', 'count-changed'])

const props = defineProps({
  taggableId: {
    type: [Number, String],
    required: true,
  },
  taggableType: {
    type: String,
    required: true,
  },
  initialTags: {
    type: Array,
    default: () => [],
  },
  title: {
    type: String,
    default: 'Tags',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  context: {
    type: String,
    default: 'taggable-tags',
  },
})

const tagsStore = useTagsStore(`${props.context}_${String(props.taggableType).replace(/[^A-Za-z0-9]/g, '_')}_${props.taggableId}`)
const attachedTags = ref([])
const tagQuery = ref('')
const isAddingTypedTag = ref(false)

const normalizedTaggableId = computed(() => Number(props.taggableId))
const normalizedTaggableType = computed(() => String(props.taggableType ?? '').trim())
const taggablePayload = computed(() => ({
  taggable_id: normalizedTaggableId.value,
  taggable_type: normalizedTaggableType.value,
}))
const isValidTaggable = computed(() => {
  return Number.isInteger(normalizedTaggableId.value) && normalizedTaggableId.value > 0 && normalizedTaggableType.value.length > 0
})
const attachedTagIds = computed(() => new Set(attachedTags.value.map((tag) => Number(tag.id))))
const normalizedTagQuery = computed(() => tagQuery.value.trim())
const matchingAvailableTags = computed(() => {
  const query = normalizedTagQuery.value.toLocaleLowerCase()

  return tagsStore.items
    .filter((tag) => !attachedTagIds.value.has(Number(tag.id)))
    .filter((tag) => query === '' || String(tag.name ?? '').toLocaleLowerCase().includes(query))
    .slice(0, 8)
})
const exactQueryTag = computed(() => {
  const query = normalizedTagQuery.value.toLocaleLowerCase()

  if (query === '') {
    return null
  }

  return tagsStore.items.find((tag) => String(tag.name ?? '').trim().toLocaleLowerCase() === query) ?? null
})
const canAddTypedTag = computed(() => normalizedTagQuery.value.length > 0 && isValidTaggable.value)
const loadErrorMessage = computed(() => {
  if (!tagsStore.error) {
    return null
  }

  return tagsStore.error.response?.data?.message ?? tagsStore.error.message ?? 'Unable to load tags.'
})

const fetchTags = async () => {
  if (!isValidTaggable.value) {
    attachedTags.value = []
    emit('count-changed', 0)
    return
  }

  const [, attachedResponse] = await Promise.all([
    tagsStore.fetchItems({
      clear: true,
      order_by: 'name',
      order_direction: 'asc',
    }),
    axios.get(tagsStore.api, {
      params: {
        ...taggablePayload.value,
      },
    }),
  ])

  attachedTags.value = Array.isArray(attachedResponse.data) ? attachedResponse.data : []
  emit('count-changed', attachedTags.value.length)
}

const attachTag = async (tag) => {
  if (props.readonly || !isValidTaggable.value || attachedTagIds.value.has(Number(tag?.id))) {
    return
  }

  await tagsStore.attachTag(tag.id, taggablePayload.value)
  await fetchTags()
  emit('changed', attachedTags.value)
}

const detachTag = async (tag) => {
  if (props.readonly || !isValidTaggable.value) {
    return
  }

  await tagsStore.detachTag(tag.id, taggablePayload.value)
  await fetchTags()
  emit('changed', attachedTags.value)
}

const addTypedTag = async () => {
  if (!canAddTypedTag.value || isAddingTypedTag.value) {
    return
  }

  isAddingTypedTag.value = true

  try {
    const tag = exactQueryTag.value ?? await tagsStore.itemCreate({
      name: normalizedTagQuery.value,
      description: '',
      icon_library: 'lucide',
    })

    await attachTag(tag)
    tagQuery.value = ''
  } finally {
    isAddingTypedTag.value = false
  }
}

const resolveTagStyle = (tag) => {
  const color = String(tag?.color ?? '').trim()

  if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
    return {}
  }

  return {
    borderColor: color,
    color,
  }
}

watch(
  () => props.initialTags,
  (tags) => {
    attachedTags.value = Array.isArray(tags) ? tags : []
    emit('count-changed', attachedTags.value.length)
  },
  { immediate: true },
)

watch(
  () => [props.taggableId, props.taggableType],
  async () => {
    tagQuery.value = ''
    await fetchTags()
  },
)

onMounted(async () => {
  await fetchTags()
})

defineExpose({
  fetchTags,
})
</script>
